'use client';

import React from 'react';
import type { Slide } from '../lib/types';
import { cn } from '@/lib/utils';
import { Trash2, Copy, GripVertical } from 'lucide-react';

const SHAPE_PATHS: Record<string, string> = {
    triangle: 'M 12,4 L 20,18 H 4 L 12,4 Z',
    rightTriangle: 'M 4,4 V 20 H 20 L 4,4 Z',
    rhombus: 'M 12,4 L 20,12 L 12,20 L 4,12 L 12,4 Z',
    parallelogram: 'M 7,4 H 21 L 17,20 H 3 L 7,4 Z',
    trapezoid: 'M 6,4 H 18 L 21,20 H 3 L 6,4 Z',
    pentagon: 'M 12,3 L 21,9 V 19 H 3 V 9 L 12,3 Z',
    hexagon: 'M 12,3 L 20,7.5 V 16.5 L 12,21 L 4,16.5 V 7.5 L 12,3 Z',
    heptagon: 'M 12,3 L 18.5,6 L 21,13 L 16,20 H 8 L 3,13 L 5.5,6 L 12,3 Z',
    octagon: 'M 8.5,3 H 15.5 L 21,8.5 V 15.5 L 15.5,21 H 8.5 L 3,15.5 V 8.5 L 8.5,3 Z',
    heart: 'M 12,21.35 L 10.55,20.03 C 5.4,15.36 2,12.28 2,8.5 2,5.42 4.42,3 7.5,3 C 9.24,3 10.91,3.81 12,5.09 C 13.09,3.81 14.76,3 16.5,3 C 19.58,3 22,5.42 22,8.5 C 22,12.28 18.6,15.36 13.45,20.04 L 12,21.35 Z',
    smiley: 'M 12,3 A 9,9 0 1,0 12,21 A 9,9 0 1,0 12,3 M 9,9 A 1.5,1.5 0 1,1 9,10.5 A 1.5,1.5 0 1,1 9,9 M 15,9 A 1.5,1.5 0 1,1 15,10.5 A 1.5,1.5 0 1,1 15,9 M 8,15 C 8,15 9.5,17 12,17 C 14.5,17 16,15 16,15',
    sun: 'M 12,7 A 5,5 0 1,1 12,17 A 5,5 0 1,1 12,7 M 12,2 V 4 M 12,20 V 22 M 4,12 H 2 M 22,12 H 20 M 19.07,4.93 L 17.66,6.34 M 6.34,17.66 L 4.93,19.07 M 4.93,4.93 L 6.34,6.34 M 17.66,17.66 L 19.07,19.07',
    moon: 'M 12,3 A 9,9 0 1,0 12,21 A 7,7 0 0,1 12,3 Z',
    circle: 'M 12,3 A 9,9 0 1,1 12,21 A 9,9 0 1,1 12,3 Z',
    arrowRight: 'M 4,11 H 15.5 L 12,7.5 L 13.5,6 L 19.5,12 L 13.5,18 L 12,16.5 L 15.5,13 H 4 Z',
    arrowLeft: 'M 20,11 H 8.5 L 12,7.5 L 10.5,6 L 4.5,12 L 10.5,18 L 12,16.5 L 8.5,13 H 20 Z',
    arrowUp: 'M 13,20 V 8.5 L 16.5,12 L 18,10.5 L 12,4.5 L 6,10.5 L 7.5,12 L 11,8.5 V 20 Z',
    arrowDown: 'M 11,4 V 15.5 L 7.5,12 L 6,13.5 L 12,19.5 L 18,13.5 L 16.5,12 L 13,15.5 V 4 Z',
    arrowLeftRight: 'M 4,12 L 8,8 V 11 H 16 V 8 L 20,12 L 16,16 V 13 H 8 V 16 L 4,12 Z',
    arrowUpDown: 'M 12,4 L 8,8 H 11 V 16 H 8 L 12,20 L 16,16 H 13 V 8 H 16 L 12,4 Z',
    star4: 'M 12,2 L 14,10 L 22,12 L 14,14 L 12,22 L 10,14 L 2,12 L 10,10 L 12,2 Z',
    star5: 'M 12,2 L 14.5,9.5 H 22 L 16,14 L 18.5,21.5 L 12,17 L 5.5,21.5 L 8,14 L 2,9.5 H 9.5 L 12,2 Z',
    star6: 'M 12,2 L 15,7 L 21,6 L 17,10.5 L 19,17 L 12,14 L 5,17 L 7,10.5 L 3,6 L 9,7 L 12,2 Z',
    star8: 'M 12,2 L 14,6 L 18,4 L 16,8 L 20,10 L 16,12 L 18,16 L 14,14 L 12,18 L 10,14 L 6,16 L 8,12 L 4,10 L 8,8 L 6,4 L 10,6 L 12,2 Z',
    cloud: 'M 17,19 C 14.8,19 13.1,17.7 12.3,15.8 C 11.4,16 10.5,16.2 9.6,16.2 C 7,16.2 4.7,14.6 3.7,12.3 C 1.5,11.8 0,9.9 0,7.6 C 0,5 2.1,2.9 4.7,2.9 C 5.3,2.9 5.8,3 6.3,3.1 C 7.6,1.4 9.6,0.3 11.9,0.3 C 12.5,0.3 13.1,0.4 13.7,0.5 C 15,1.9 15.9,3.7 16,5.7 C 18.2,6.1 20,8.1 20,10.5 C 20,13.2 17.8,15.4 15.1,15.4 C 14.8,15.4 14.5,15.4 14.2,15.3 C 14.2,15.4 14.2,15.4 14.2,15.5 C 14.2,17.4 15.5,19 17,19 Z',
    lightning: 'M 13,2 L 3,14 H 12 V 22 L 22,10 H 13 L 16,2 Z',
    plus: 'M 19,13 H 13 V 19 H 11 V 13 H 5 V 11 H 11 V 5 H 13 V 11 H 19 V 13 Z',
    minus: 'M 19,13 H 5 V 11 H 19 V 13 Z',
    multiply: 'M 19,6.41 L 17.59,5 L 12,10.59 L 6.41,5 L 5,6.41 L 10.59,12 L 5,17.59 L 6.41,19 L 12,13.41 L 17.59,19 L 19,17.59 L 13.41,12 L 19,6.41 Z',
    divide: 'M 12,7 A 2,2 0 1,1 12,11 A 2,2 0 1,1 12,7 M 12,17 A 2,2 0 1,1 12,21 A 2,2 0 1,1 12,17 M 5,11 H 19 V 13 H 5 V 11 Z',
    equal: 'M 19,10 H 5 V 8 H 19 V 10 M 19,16 H 5 V 14 H 19 V 16 Z'
};
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SidebarProps {
    slides: Slide[];
    currentSlideIndex: number;
    onSelectSlide: (index: number) => void;
    onDeleteSlide: (index: number) => void;
    onDuplicateSlide: (index: number) => void;
    onReorderSlides: (oldIndex: number, newIndex: number) => void;
    uiScale: number;
    appBgColor?: string;
}

const SortableSlide = ({
    slide,
    index,
    isActive,
    onSelect,
    onDelete,
    onDuplicate,
    showDelete,
    uiScale,
    appBgColor
}: {
    slide: Slide,
    index: number,
    isActive: boolean,
    onSelect: () => void,
    onDelete: () => void,
    onDuplicate: () => void,
    showDelete: boolean,
    uiScale: number,
    appBgColor: string
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: slide.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 1,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div
            ref={setNodeRef}
            onClick={onSelect}
            className={cn(
                "cursor-pointer border-[1.5px] border-solid p-2 transition-all duration-300 group relative select-none",
                isActive
                    ? "bg-white"
                    : "border hover:border-slate-200 bg-white/50 hover:bg-white"
            )}
            style={{
                ...style,
                border: isActive ? `1px solid ${appBgColor}` : "1px solid transparent"
            }}
        >
            <div
                className={cn("text-xs font-bold mb-1 flex items-center justify-between transition-colors", !isActive && "text-slate-400")}
                style={{ color: isActive ? appBgColor : undefined }}
            >
                <div className="flex items-center gap-1">
                    <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-100 text-slate-300 hover:text-slate-500">
                        <GripVertical size={14} />
                    </div>
                    <span>SLIDE {index + 1}</span>
                </div>
                <div className="flex items-center gap-1">
                    {isActive && <span className="h-1.5 w-1.5" style={{ backgroundColor: appBgColor }} />}
                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                            className="p-1 hover:bg-blue-50 text-slate-400 hover:text-blue-500 transition-all"
                            title="Duplicate Slide"
                        >
                            <Copy size={13} />
                        </button>
                        {showDelete && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                className="p-1 hover:bg-red-50 text-red-400 hover:text-red-500 transition-all"
                                title="Delete Slide"
                            >
                                <Trash2 size={13} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="aspect-video bg-white flex items-center justify-center border border-slate-200 overflow-hidden relative group-hover:shadow-sm transition-shadow">
                {/* Scaled Preview of Slide Content */}
                <div
                    className="origin-center pointer-events-none select-none shrink-0"
                    style={{
                        width: '1200px',
                        height: '675px',
                        transform: `scale(${(Math.max(220, 256 * uiScale) - 48) / 1200})`, // Scale to fill sidebar width
                        backgroundColor: '#ffffff'
                    }}
                >
                    {slide.elements.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((el) => {
                        const style: React.CSSProperties = {
                            position: 'absolute',
                            left: el.x,
                            top: el.y,
                            width: el.width,
                            height: el.height,
                            opacity: el.opacity ?? 1,
                        };

                        if (el.type === 'text') {
                            return (
                                <div
                                    key={el.id}
                                    style={{
                                        ...style,
                                        fontSize: el.fontSize,
                                        fontFamily: el.fontFamily,
                                        color: el.color,
                                        textAlign: el.textAlign || 'left',
                                        fontWeight: el.isBold ? 'bold' : 'normal',
                                        fontStyle: el.isItalic ? 'italic' : 'normal',
                                        lineHeight: 1.2,
                                        overflow: 'hidden',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                    {el.content}
                                </div>
                            );
                        } else if (el.type === 'shape') {
                            const pathData = SHAPE_PATHS[el.shapeType] || (el.shapeType === 'ellipse' ? SHAPE_PATHS.circle : null);
                            if (pathData) {
                                return (
                                    <svg
                                        key={el.id}
                                        viewBox="0 0 24 24"
                                        style={{
                                            ...style,
                                            fill: el.fill
                                        }}
                                        preserveAspectRatio="none"
                                    >
                                        <path d={pathData} />
                                    </svg>
                                );
                            }
                            return (
                                <div
                                    key={el.id}
                                    style={{
                                        ...style,
                                        backgroundColor: el.fill,
                                        borderRadius: el.shapeType === 'ellipse' ? '50%' : '0'
                                    }}
                                />
                            );
                        } else if (el.type === 'image') {
                            return (
                                <img
                                    key={el.id}
                                    src={el.src}
                                    alt=""
                                    style={{
                                        ...style,
                                        objectFit: 'contain'
                                    }}
                                />
                            );
                        }
                        return null;
                    })}
                </div>
            </div>
        </div>
    );
};

export const Sidebar: React.FC<SidebarProps> = ({
    slides,
    currentSlideIndex,
    onSelectSlide,
    onDeleteSlide,
    onDuplicateSlide,
    onReorderSlides,
    uiScale,
    appBgColor = '#B7472A'
}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = slides.findIndex((s) => s.id === active.id);
            const newIndex = slides.findIndex((s) => s.id === over?.id);
            onReorderSlides(oldIndex, newIndex);
        }
    };

    return (
        <div
            className="bg-white border-r border-slate-100 overflow-y-auto p-4 flex flex-col gap-4"
            style={{ width: `${Math.max(220, 256 * uiScale)}px` }}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={slides.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {slides.map((slide, index) => (
                        <SortableSlide
                            key={slide.id}
                            slide={slide}
                            index={index}
                            isActive={index === currentSlideIndex}
                            onSelect={() => onSelectSlide(index)}
                            onDelete={() => onDeleteSlide(index)}
                            onDuplicate={() => onDuplicateSlide(index)}
                            showDelete={slides.length > 1}
                            uiScale={uiScale}
                            appBgColor={appBgColor}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};
